/**
 * Authentication API Service
 * Handles backend user registration and profile management
 */

interface RegisterUserRequest {
  email: string;
  name: string;
  firebaseUid: string;
  image?: string;
}

interface UserProfile {
  id: string;
  email: string;
  name: string;
  image?: string | null;
  emailVerified: Date | null;
  timezone?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

interface GetCurrentUserResponse {
  user?: UserProfile;
  firebase?: {
    uid: string;
    email: string | null;
    emailVerified: boolean;
    name: string | null;
    picture: string | null;
  };
  needsRegistration?: boolean;
  betaAccessDenied?: {
    code: 'BETA_PENDING' | 'BETA_REJECTED' | 'BETA_REQUIRED';
    message: string;
  };
}

/**
 * Register a new user in the database after Firebase authentication
 */
export async function registerUser(
  idToken: string,
  userData: RegisterUserRequest
): Promise<UserProfile> {
  const response = await fetch('/api/auth/register', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Registration failed' }));
    throw new Error(error.error || 'Failed to register user');
  }

  const data = await response.json();
  return data.user;
}

/**
 * Get current user profile from database
 *
 * Handles BETA flag enforcement:
 * - 403 with BETA_PENDING/BETA_REJECTED/BETA_REQUIRED: Returns betaAccessDenied
 * - Caller should handle by signing user out and showing appropriate UI
 */
export async function getCurrentUser(idToken: string): Promise<GetCurrentUserResponse> {
  const response = await fetch('/api/auth/me', {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (response.status === 404) {
    const data = await response.json();
    return { needsRegistration: data.needsRegistration || false };
  }

  if (response.status === 403) {
    const data = await response.json();
    // Check if it's a beta access denial
    if (data.error && ['BETA_PENDING', 'BETA_REJECTED', 'BETA_REQUIRED'].includes(data.error)) {
      console.warn('[getCurrentUser] Beta access denied:', data.error, data.message);
      return {
        betaAccessDenied: {
          code: data.error as 'BETA_PENDING' | 'BETA_REJECTED' | 'BETA_REQUIRED',
          message: data.message || 'Beta access required'
        }
      };
    }
    // Other 403 errors
    throw new Error(data.message || 'Access denied');
  }

  if (!response.ok) {
    throw new Error('Failed to fetch user profile');
  }

  return await response.json();
}

interface UpdateProfileRequest {
  name?: string;
  image?: string;
}

/**
 * Update user profile in database
 */
export async function updateUserProfile(
  idToken: string,
  updates: UpdateProfileRequest
): Promise<UserProfile> {
  const response = await fetch('/api/auth/profile', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Update failed' }));
    throw new Error(error.error || 'Failed to update profile');
  }

  const data = await response.json();
  return data.user;
}

/**
 * Upload profile image to Vercel Blob Storage
 * @param file - Image file to upload
 * @param idToken - Firebase ID token for authentication
 * @returns Public download URL
 */
export async function uploadProfileImage(file: File, idToken: string): Promise<string> {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  // Validate file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    throw new Error('Image size must be less than 5MB');
  }

  // Create FormData to send file
  const formData = new FormData();
  formData.append('file', file);

  // Upload to API endpoint
  const response = await fetch('/api/profile-image/upload', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(error.message || error.error || 'Failed to upload image');
  }

  const data = await response.json();
  return data.url;
}
